// ios.jsx — Apple-style UI primitives: the app's emerging design language.
//
// Introduced on the Coffee page (Coffee-first, app-wide-ready). SF typography
// (font-sf / font-round), iOS inset-grouped lists, one tint (ember), and space
// doing the work instead of borders. Built on Pronto's existing palette so the
// Apple grammar stays warm rather than clinical.
import { ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { StoragePhoto } from "./StoragePhoto";

/* ---------- Large title header (iOS nav large-title) ---------- */
export function LargeTitle({ title, subtitle, trailing }) {
  return (
    <div className="pt-2 pb-3">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-round font-bold text-[32px] leading-none tracking-[-0.026em] text-ink-700">
          {title}
        </h1>
        {trailing}
      </div>
      {subtitle && <div className="font-sf text-[14px] text-warm-500 mt-1.5">{subtitle}</div>}
    </div>
  );
}

/* ---------- Segmented control ---------- */
export function Segmented({ options, value, onChange, className = "" }) {
  return (
    <div className={`flex bg-warm-200 rounded-[10px] p-0.5 ${className}`}>
      {options.map((o) => {
        const on = o.id === value;
        return (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            className={`flex-1 rounded-[8px] py-1.5 font-sf text-[13px] font-semibold transition-colors ${
              on ? "bg-white text-ink-700 shadow-[0_1px_3px_rgba(0,0,0,0.14),0_0_0_0.5px_rgba(0,0,0,0.04)]" : "text-warm-500"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Stepper ---------- */
export function Stepper({ value, onChange, min = 1, max = 10, step = 1, format }) {
  const clamp = (v) => Math.max(min, Math.min(max, Math.round(v / step) * step));
  return (
    <div className="flex items-center bg-warm-200 rounded-xl overflow-hidden select-none">
      <button
        onClick={() => onChange(clamp(value - step))}
        className="w-9 h-11 text-[22px] leading-none text-ember-500 disabled:text-warm-500/40"
        disabled={value <= min}
        aria-label="Decrease"
      >
        −
      </button>
      <span className="font-round font-bold text-[17px] min-w-[28px] text-center text-ink-700 tabular">
        {format ? format(value) : value}
      </span>
      <button
        onClick={() => onChange(clamp(value + step))}
        className="w-9 h-11 text-[22px] leading-none text-ember-500 disabled:text-warm-500/40"
        disabled={value >= max}
        aria-label="Increase"
      >
        +
      </button>
    </div>
  );
}

/* ---------- Spec tiles (compact readout row) ---------- */
export function SpecTiles({ items, className = "" }) {
  return (
    <div className={`grid gap-2 ${className}`} style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0,1fr))` }}>
      {items.map((it, i) => (
        <div key={i} className="bg-warm-200 rounded-[13px] py-2.5 px-1.5 text-center">
          <div className="font-sf text-[10px] font-semibold uppercase tracking-[0.04em] text-warm-500">{it.label}</div>
          <div className="font-round font-semibold text-[18px] leading-tight text-ink-700 tabular mt-0.5">{it.value}</div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Buttons ---------- */
export function PrimaryButton({ children, onClick, tone = "tint", disabled, className = "" }) {
  const bg = tone === "ink" ? "bg-ink-700" : "bg-ember-500";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`h-12 rounded-[14px] ${bg} text-white font-sf font-semibold text-[16px] flex items-center justify-center gap-2 disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  );
}

export function IconButton({ children, onClick, disabled, title, className = "" }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`w-12 h-12 rounded-[14px] bg-warm-200 text-ember-500 grid place-items-center disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  );
}

/* ---------- Inset grouped list ---------- */
export function GroupLabel({ children, className = "" }) {
  return (
    <div className={`font-sf text-[12.5px] font-semibold uppercase tracking-[0.03em] text-warm-500 px-1 mt-5 mb-2 ${className}`}>
      {children}
    </div>
  );
}

export function Group({ children, className = "" }) {
  return (
    <div className={`bg-white rounded-2xl overflow-hidden border border-[var(--border-subtle)] ${className}`}>
      {children}
    </div>
  );
}

const CELL_TONE = { tint: "bg-ember-500", sage: "bg-sage-500", neutral: "bg-warm-500" };

export function Cell({ icon, iconTone = "tint", label, labelTint, value, valueTint, chevron, onClick, children }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 min-h-[48px] text-left border-b-[0.5px] border-[var(--border-subtle)] last:border-b-0"
    >
      {icon && (
        <span className={`w-[26px] h-[26px] rounded-[7px] grid place-items-center flex-shrink-0 text-white ${CELL_TONE[iconTone]}`}>
          {icon}
        </span>
      )}
      <span className={`font-sf text-[15px] flex-1 ${labelTint ? "text-ember-500 font-medium" : "text-ink-600"}`}>{label}</span>
      {children}
      {value != null && (
        <span className={`font-round font-semibold text-[15px] tabular ${valueTint ? "text-ember-500" : "text-ink-700"}`}>{value}</span>
      )}
      {chevron && <ChevronRight size={15} className="text-warm-500 ml-1 flex-shrink-0" />}
    </Tag>
  );
}

/* ---------- Bean shelf ---------- */

// Roast-level → bag-art gradient. Darker roast, darker bag.
const ROAST_ART = {
  light: "linear-gradient(155deg,#a9744a,#5c3820)",
  "medium-light": "linear-gradient(155deg,#98663f,#4d2e1a)",
  medium: "linear-gradient(155deg,#8a5c38,#452a19)",
  "medium-dark": "linear-gradient(155deg,#6f4c34,#3a2417)",
  dark: "linear-gradient(155deg,#513a2c,#2a1c14)",
};

// Roast-level → bag-art gradient, shared by the shelf card and the bean sheet.
export function roastArt(roastLevel) {
  return ROAST_ART[roastLevel] || ROAST_ART.medium;
}

function FreshnessRing({ pct, warn }) {
  const r = 15, C = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, pct ?? 0));
  return (
    <svg width="26" height="26" viewBox="0 0 36 36" className="absolute top-2.5 right-2.5">
      <circle cx="18" cy="18" r={r} fill="none" stroke="rgba(255,255,255,0.28)" strokeWidth="3.4" />
      <circle
        cx="18" cy="18" r={r} fill="none"
        stroke={warn ? "#E0805F" : "#9CAE94"}
        strokeWidth="3.4" strokeLinecap="round"
        strokeDasharray={C} strokeDashoffset={C * (1 - p / 100)}
        transform="rotate(-90 18 18)"
      />
    </svg>
  );
}

export function BagCard({ bean, forecast, rating, open = false, onToggleOpen, onClick }) {
  const pct = forecast?.pct_remaining;
  const warn = forecast?.should_reorder || (pct != null && pct <= 20);
  const art = ROAST_ART[bean?.roast_level] || ROAST_ART.medium;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(); } }}
      className="flex-shrink-0 w-[120px] text-left cursor-pointer"
    >
      <div
        className={`h-[152px] rounded-[20px] relative overflow-hidden p-3 flex flex-col justify-end shadow-[0_10px_22px_-12px_rgba(35,25,15,0.55)] ${open ? "ring-2 ring-sage-500 ring-offset-2 ring-offset-[var(--bg-page,#faf7f2)]" : ""}`}
        style={{ background: art }}
      >
        {bean?.photo_path && (
          <>
            <StoragePhoto path={bean.photo_path} className="absolute inset-0 w-full h-full object-cover" fallback={null} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.62), rgba(0,0,0,0.02) 60%)" }} />
          </>
        )}
        <div className="absolute inset-x-0 top-0 h-4 bg-black/20" />
        {onToggleOpen && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleOpen(); }}
            className={`absolute top-2 left-2 z-10 font-sf text-[9px] font-bold uppercase tracking-[0.08em] rounded-full px-2 py-0.5 shadow-sm transition-colors ${
              open ? "bg-sage-500 text-white" : "bg-black/40 text-white/85 backdrop-blur-sm"
            }`}
            title={open ? "In your container — tap to close" : "Tap to mark open"}
          >
            {open ? "Open" : "+ Open"}
          </button>
        )}
        {pct != null && <FreshnessRing pct={pct} warn={warn} />}
        {bean?.roaster && (
          <div className="relative font-sf text-[9px] font-bold uppercase tracking-[0.09em] text-white/75 truncate">{bean.roaster}</div>
        )}
        <div className="relative font-round font-bold text-[15px] leading-[1.04] text-white mt-0.5 drop-shadow-[0_1px_4px_rgba(0,0,0,0.35)] line-clamp-2">
          {bean?.name}
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-2 px-0.5">
        {rating ? (
          <span className="text-ember-500 text-[11px] tracking-[-1px]">{"★".repeat(rating)}{"☆".repeat(Math.max(0, 5 - rating))}</span>
        ) : bean?.roast_level ? (
          <span className="font-sf text-[10px] text-warm-500 capitalize">{bean.roast_level.replace("-", " ")}</span>
        ) : <span />}
        {pct != null && <span className="ml-auto font-sf text-[11px] font-semibold text-warm-500 tabular">{pct}%</span>}
      </div>
    </div>
  );
}

export function AddBagCard({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex-shrink-0 w-[120px] h-[152px] rounded-[20px] bg-white border border-[var(--border-subtle)] grid place-items-center text-ember-500 text-[30px] font-extralight"
      aria-label="Add a bean"
    >
      +
    </button>
  );
}

/* ---------- Slide-up sheet (iOS presentation) ---------- */
export function Sheet({ open, onClose, children }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-x-0 bottom-0 z-[44] flex justify-center pointer-events-none"
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 34, stiffness: 340 }}
          >
            <div className="pointer-events-auto w-full max-w-[460px] bg-warm-100 rounded-t-[28px] max-h-[94vh] overflow-hidden flex flex-col font-sf shadow-[0_-8px_40px_-8px_rgba(0,0,0,0.4)]">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
